/**
 * Defines sagas related to Primary Navigation bar feature.
 */
import {
  put, call, delay, select,
} from 'redux-saga/effects';
import get from 'lodash/get';
import {
  getMFAConfigService, getAppDetailsService, getUserDetailsService, getUnreadMsgsCount,
  getGuestUserProfileService,
} from 'services/App.service';
import {
  getMFAConfigFromJson, setUserDetails, getPollingDelayTime, shouldShowEvoneBanner,
} from 'utils/app.utils';
import * as types from 'constants/ActionTypes';
import { TOGGLE_SIDEBAR_HIDE, TOGGLE_SIDEBAR_SHOW, SET_FULL_SCREEN } from 'constants/ActionTypes';
import event from 'event';
import { EVENTS } from 'constants/AppConstants';
import { SET_SHARED_CONTEXT } from 'features/shared-startup/SharedStartup.actions';
import { fetchUserEntitledApps } from 'features/user-entitled-apps/UserEntitledApps.actions';
import {
  pollUnreadNotifications,
  pollUnreadNotificationsResponded,
  setEvoneBannerVisibility,
} from './SideBar.actions';

// #genai Single place for Evone banner visibility (LD + third-party list); replaces reducer + MfContainer duplicate logic
export function* syncEvoneBannerVisibilitySaga(action) {
  if (action.type === types.FETCH_USER_ENTITLED_APPS_ERROR) {
    const current = yield select((s) => s.SideBar.evoneBannerVisible);
    if (current) {
      yield put(setEvoneBannerVisibility(false));
    }
    return;
  }

  const {
    thirdPartyAppsFetched,
    thirdPartyAppList,
    featureFlagsLoaded,
    mfaResponseReceived,
    ldShowEvoneBanner,
    evoneBannerVisible,
  } = yield select((state) => ({
    thirdPartyAppsFetched: state.UserEntitledApps?.thirdPartyAppsFetched ?? false,
    thirdPartyAppList: state.UserEntitledApps?.thirdPartyAppList ?? [],
    featureFlagsLoaded: state.SideBar.featureFlagsLoaded,
    mfaResponseReceived: state.SideBar.mfaResponseReceived,
    ldShowEvoneBanner: state.SideBar.ldShowEvoneBanner ?? true,
    evoneBannerVisible: state.SideBar.evoneBannerVisible,
  }));

  if (!thirdPartyAppsFetched || !featureFlagsLoaded || !mfaResponseReceived) {
    return;
  }

  const next = shouldShowEvoneBanner(thirdPartyAppList, ldShowEvoneBanner);
  if (next !== evoneBannerVisible) {
    yield put(setEvoneBannerVisibility(next));
  }
}

// #genai On `/start?source=actv` the entitled-apps fetch is owned by UserEntitledApps.component
// (handleUserDetailsFetched dispatches it only when no activation hand-off is required). Firing
// it unconditionally here introduces a race that can cancel the devportal/onboarding redirect.
function isOnActivationStartRoute() {
  if (typeof window === 'undefined' || !window.location) {
    return false;
  }
  try {
    const params = new URLSearchParams(window.location.search || '');
    return window.location.pathname === '/start' && params.get('source') === 'actv';
  } catch (e) {
    return false;
  }
}

export function* getMFAConfigSaga() {
  try {
    const response = yield call(getMFAConfigService);
    const mfaConfig = getMFAConfigFromJson(response);
    yield put({
      type: types.MFA_CONFIG_RESPONDED,
      mfaConfig,
      defaultApp: (Array.isArray(mfaConfig) && mfaConfig[0]) || {},
      mfaResponseReceived: true,
    });
    // #genai EVONE: ingredients come from third-party entitled-apps API, not viewpoint /entitlements
    // Skip on `/start?source=actv` so the activation hand-off in UserEntitledApps owns the fetch.
    // On a redirect to `/` (full page reload), this saga runs again with onActivationStart=false
    // and the entitled-apps list (and EVONE banner) populate normally.
    if (!isOnActivationStartRoute()) {
      yield put(fetchUserEntitledApps());
    }
  } catch (error) {
    // #genai empty third-party so sidebar can mount + evone banner stays false without waiting forever
    yield put({ type: types.FETCH_USER_ENTITLED_APPS_SUCCESS, thirdPartyAppList: [] });
    yield put({ type: types.MFA_CONFIG_ERROR, error });
  }
}

export function* getUserDetailsSaga({ fetchUserApp }) {
  try {
    const userData = yield call(getUserDetailsService, fetchUserApp);
    const userDetails = {
      firstName: userData.firstName ? userData.firstName : '',
      lastName: userData.lastName ? userData.lastName : '',
      title: userData.title ? userData.title : '',
      ...userData,
    };
    const initiatorDetails = {
      initiatorUserId: userData.initiatorUserId ? userData.initiatorUserId : '',
      initiatorUserName: userData.initiatorUserName ? userData.initiatorUserName : '',
      initiatorOrgId: userData.initiatorOrgId ? userData.initiatorOrgId : '',
      initiatorFirstName: userData.initiatorFirstName ? userData.initiatorFirstName : '',
      initiatorLastName: userData.initiatorLastName ? userData.initiatorLastName : '',
    };
    const isImpersonated = initiatorDetails.initiatorUserId !== '';
    yield put({
      type: types.USER_DETAILS_RESPONDED, userDetails, initiatorDetails, isImpersonated,
    });
    setUserDetails(userDetails);
  } catch (error) {
    yield put({ type: types.USER_DETAILS_ERROR });
  }
}

// #genai guest user profile via v2 account-management API.
// Maps v2 field names to the shape the rest of the app expects (userName, organization.id).
export function* getGuestUserDetailsSaga() {
  try {
    const userData = yield call(getGuestUserProfileService);
    const userDetails = {
      userName: userData.username || userData.email || '',
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: userData.email || '',
      organization: { id: userData.organizationId || '' },
    };
    yield put({
      type: types.USER_DETAILS_RESPONDED, userDetails, initiatorDetails: {}, isImpersonated: false,
    });
    setUserDetails(userDetails);
  } catch (error) {
    yield put({ type: types.USER_DETAILS_ERROR });
  }
}

export function* getLoadersSaga() {
  try {
    const loaders = window.ev_vp.templates;
    yield put({ type: types.LOADERS_RESPONDED, loaders });
  } catch (error) {
    yield put({ type: types.LOADERS_RESPONDED, loaders: {} });
  }
}

export function* pollUnreadMsgs(action) {
  const { userId } = action.payload;
  try {
    if (userId) {
      const response = yield call(getUnreadMsgsCount, userId);
      if (response.status === 200) {
        yield put(pollUnreadNotificationsResponded({ msgsCount: get(response, 'json.count', 0) }));
      } else {
        yield put(pollUnreadNotificationsResponded({ msgsCount: 0 }));
      }
      const delayTime = getPollingDelayTime();
      yield delay(delayTime * 1000);
      yield put(pollUnreadNotifications(action.payload));
    }
  } catch (error) {
    yield put(pollUnreadNotificationsResponded({ msgsCount: 0 }));
  }
}

// #genai shared-resource flow: fetch app details via dedicated API
// with optional entitlement-first fallback for EV users who also have share access.
export function* fetchSharedAppConfigSaga(action) {
  const { appCode, checkEntitlementsFirst } = action.payload;
  try {
    if (checkEntitlementsFirst) {
      // Case 2a (EV user): try old entitlements first
      const entitlementResponse = yield call(getMFAConfigService);
      const mfaConfig = getMFAConfigFromJson(entitlementResponse);
      const appInEntitlements = mfaConfig.some((app) => app.appId === appCode);

      if (appInEntitlements) {
        // App found in org entitlements — normal sidebar + mount flow, nothing new needed.
        yield put({
          type: types.MFA_CONFIG_RESPONDED,
          mfaConfig,
          defaultApp: (Array.isArray(mfaConfig) && mfaConfig[0]) || {},
          mfaResponseReceived: true,
        });
        yield put(fetchUserEntitledApps());
        return;
      }

      // App NOT in org entitlements — access is only through share.
      // Dispatch the user's existing org apps for sidebar (unchanged).
      // Then call new API and store appUrl in SharedStartup for direct mount.
      yield put({
        type: types.MFA_CONFIG_RESPONDED,
        mfaConfig,
        defaultApp: (Array.isArray(mfaConfig) && mfaConfig[0]) || {},
        mfaResponseReceived: true,
      });
      yield put(fetchUserEntitledApps());

      const appDetails = yield call(getAppDetailsService, appCode);
      yield put({ type: SET_SHARED_CONTEXT, payload: { appUrl: get(appDetails, 'appUrl', ''), appName: get(appDetails, 'name', '') } });
      return;
    }

    // Case 1: Guest (non-EV) user — call new API, store appUrl in SharedStartup for direct mount,
    // keep mfaConfig empty so the app doesn't appear in the sidebar.
    const appDetails = yield call(getAppDetailsService, appCode);
    yield put({ type: SET_SHARED_CONTEXT, payload: { appUrl: get(appDetails, 'appUrl', ''), appName: get(appDetails, 'name', '') } });
    yield put({
      type: types.MFA_CONFIG_RESPONDED,
      mfaConfig: [],
      defaultApp: {},
      mfaResponseReceived: true,
    });
    yield put(fetchUserEntitledApps());
  } catch (error) {
    yield put({ type: types.MFA_CONFIG_ERROR, error });
  }
}

export function* emitFullscreenSaga(action) {
  const { type, payload } = action;
  const { shouldSidebarRender, shouldHeaderRender } = yield select((state) => state.SideBar);
  let sidebarVisible = shouldSidebarRender;
  let headerVisible = shouldHeaderRender;

  switch (type) {
    case TOGGLE_SIDEBAR_HIDE:
      sidebarVisible = false;
      headerVisible = false;
      break;
    case TOGGLE_SIDEBAR_SHOW:
      sidebarVisible = true;
      headerVisible = true;
      break;
    case SET_FULL_SCREEN: {
      const { hideSidebar = false, hideHeader = false } = payload;
      sidebarVisible = !hideSidebar;
      headerVisible = !hideHeader;
      break;
    }
    default:
      break;
  }
  event.getEventInstance().emit(
    EVENTS.FULL_SCREEN,
    {
      sidebarVisible,
      headerVisible,
      fullscreen: !headerVisible,
    },
  );
}
